import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { PromptCategory } from '../../types';
import { getPrompts } from '../../services/api';
import { useAuthToken } from '../../services/auth';
import './PromptLibrary.css';

interface Props {
  onBack: () => void;
  onSelectPrompt: (prompt: string) => void;
  config: { apiUrl: string };
}

function PromptLibrary({ onBack, onSelectPrompt, config }: Props) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [categories, setCategories] = useState<PromptCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getToken, account } = useAuthToken();
  
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const userId = account?.username || account?.localAccountId || 'anonymous';
        
        const data = await getPrompts({
          apiUrl: config.apiUrl,
          token,
          userId
        });
        
        setCategories(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch prompts:', err);
        setError('Failed to load prompts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPrompts();
  }, [config.apiUrl, getToken, account]);

  const filteredCategories = categories.map(category => ({
    ...category,
    prompts: category.prompts.filter(prompt =>
      searchTerm === '' ||
      prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.subheader.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.prompts.length > 0);

  if (loading) {
    return (
      <div className="library-container loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
        <p>Loading prompts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="library-container error-container">
        <p className="error-message">{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="library-container">
      <div className="library-header">
        <div className="search-container">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder={t('library.searchPrompts')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="categories-container">
        <button
          className={`category-button ${selectedCategory === null ? 'active' : ''}`}
          onClick={() => setSelectedCategory(null)}
        >
          {t('library.allCategories')}
        </button>
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>

      <div className="prompts-grid">
        {(selectedCategory === null ? filteredCategories : filteredCategories.filter(c => c.id === selectedCategory))
          .map(category => (
            <div key={category.id} className="category-section">
              <h2 className="category-title">{category.name}</h2>
              <div className="category-prompts">
                {category.prompts.map(prompt => (
                  <div key={prompt.id} className="prompt-card">
                    <div className="prompt-header">
                      <h3>{prompt.title}</h3>
                      <h4>{prompt.subheader}</h4>
                    </div>
                    <p className="prompt-description">{prompt.description}</p>
                    <button
                      className="use-prompt-button"
                      onClick={() => {
                        onSelectPrompt(prompt.prompt);
                        onBack();
                      }}
                    >
                      {t('library.usePrompt')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

export default PromptLibrary; 